<script setup lang="ts">
const route = useRoute()

const drawerStore = useRouterDrawerStore()
const { closeRouteInDrawer, closeAllRoutesInDrawer } = drawerStore
const { isDrawerOpen, drawerComponent, drawerName, drawerPropList, middleViews } =
  storeToRefs(drawerStore)

/**
 * @method handleClose
 * @param event {number | 'all'} Event parameter that determines which drawer(s) to close
 * @description Handles closing drawer views. If event is 'all', closes all open drawer views.
 * Otherwise closes the specific drawer at index (event-1). The event parameter is 1-based
 * while the drawer array is 0-based, so we subtract 1 from the event to get the correct index.
 * This method is called by the Drawer component's close event.
 */
function handleClose(event: number | 'all') {
  if (event === 'all') {
    closeAllRoutesInDrawer()
    return
  }
  closeRouteInDrawer(event - 1)
}

/**
 * @description Adds noScroll to the meta property of the page to be opened in the Drawer.
 * This added feature prevents the page displayed in the background from scrolling up when the drawer is opened.
 * @example For example, we're on the ASM page, and the drawer we're using is related to the Issues page. It adds noScroll to the Issues page.
 * However, for this feature to work properly, the page where the drawer is used (ASM in our example) must also have the noScroll property.
 */
watch(
  () => route.fullPath,
  () => {
    if (isDrawerOpen.value) route.meta.noScroll = true
  },
  { immediate: true },
)
</script>

<template>
  <Drawer
    buttons-position="center"
    :open="isDrawerOpen"
    :title="drawerName"
    :header-icon="drawerPropList?.headerIcon"
    :header-description="drawerPropList?.headerDescription"
    size="lg"
    remove-body-padding
    :drawer-close-length="middleViews.length + 1"
    :show-footer="false"
    @close="handleClose"
  >
    <template #content>
      <KeepAlive>
        <Component :is="drawerComponent" />
      </KeepAlive>
    </template>
  </Drawer>
</template>
